import React, { useCallback, useEffect, useRef, useState } from "react";
import { isValid, parseISO, startOfDay } from "date-fns";
import { Mail, Phone, Send, X } from "lucide-react";
import DialogBoxSkeleton from "@/components/Common/DialogBoxSkeleton";
import { ChatBubble } from "@/components/contactRequests/ChatBubble";
import { ChatDaySeparator } from "@/components/contactRequests/ChatDaySeparator";
import Loader from "@/components/Loader";
import useToastStore from "@/components/Common/Toast/toastStore";
import useGlobalStore from "@/hooks/stores/useGlobalStore";
import { InterViewSlotService } from "@/services/InterViewSlot";
import pusher from "@/services/pusherService";

export function resolveChatReceiverUserId(request, currentUserId) {
  if (!request) return null;
  const c = request.candidate;
  const uid = currentUserId != null ? Number(currentUserId) : null;
  const sid = request.sender_id != null ? Number(request.sender_id) : null;
  const rid = request.receiver_id != null ? Number(request.receiver_id) : null;

  if (uid != null) {
    if (sid != null && rid != null) {
      if (sid === uid) return rid;
      if (rid === uid) return sid;
    }
    if (sid != null && rid == null) {
      if (sid !== uid) return sid;
    }
    if (rid != null && sid == null) {
      if (rid !== uid) return rid;
    }
  }

  return (
    c?.user_id ??
    c?.user?.id ??
    request.candidate_user_id ??
    request.user_id ??
    request.requester_id ??
    request.from_user_id ??
    request.applicant_user_id ??
    null
  );
}

function otherPartyUserIdFromMessage(m, currentUserId) {
  if (m == null || currentUserId == null) return null;
  const uid = Number(currentUserId);
  const sid = m.sender_id != null ? Number(m.sender_id) : null;
  const rid = m.receiver_id != null ? Number(m.receiver_id) : null;
  if (sid === uid && rid != null) return rid;
  if (rid === uid && sid != null) return sid;
  return null;
}

function inferReceiverFromMessages(list, currentUserId) {
  if (!Array.isArray(list)) return null;
  for (const m of list) {
    const o = otherPartyUserIdFromMessage(m, currentUserId);
    if (o != null) return o;
  }
  return null;
}

function inferReceiverFromChatBubbles(bubbles, currentUserId) {
  if (!Array.isArray(bubbles)) return null;
  for (const m of bubbles) {
    const raw = m.raw;
    if (!raw) continue;
    const o = otherPartyUserIdFromMessage(raw, currentUserId);
    if (o != null) return o;
  }
  return null;
}

function pickSendReceiverId({
  storedReceiverId,
  activeRequest,
  chatMessages,
  userID,
}) {
  if (storedReceiverId != null && storedReceiverId !== "") {
    const n = Number(storedReceiverId);
    if (!Number.isNaN(n)) return n;
  }
  const fromRequest = resolveChatReceiverUserId(activeRequest, userID);
  if (fromRequest != null && fromRequest !== "") {
    const n = Number(fromRequest);
    if (!Number.isNaN(n)) return n;
  }
  const fromBubbles = inferReceiverFromChatBubbles(chatMessages, userID);
  if (fromBubbles != null) return fromBubbles;
  return null;
}

function mapMessageToBubble(apiMsg, currentUserId) {
  const uid = currentUserId != null ? Number(currentUserId) : null;
  const sid = apiMsg.sender_id != null ? Number(apiMsg.sender_id) : null;
  return {
    id: apiMsg.id,
    sender: uid != null && sid === uid ? "sent" : "received",
    content: apiMsg.message ?? "",
    created_at: apiMsg.created_at,
    raw: apiMsg,
  };
}

/**
 * Echo: Echo.private(`contact-request.${userId}`).listen('.contact-request.message.sent', ...)
 * Payload may use event.message as object, or string + message_id / id on event.
 */
function apiMessageFromUserChannelEvent(event) {
  if (!event || typeof event !== "object") return null;
  const msg = event.message;
  if (msg != null && typeof msg === "object" && msg.id != null) {
    return msg;
  }
  if (typeof msg === "string") {
    const id = event.message_id ?? event.id;
    if (id == null) return null;
    return {
      id,
      message: msg,
      sender_id: event.sender_id ?? event.sender?.id,
      receiver_id: event.receiver_id,
      created_at: event.created_at,
    };
  }
  if (event.id != null && (event.body != null || event.text != null)) {
    return {
      id: event.id,
      message: event.body ?? event.text,
      sender_id: event.sender_id ?? event.sender?.id,
      receiver_id: event.receiver_id,
      created_at: event.created_at,
    };
  }
  return null;
}

function messageSortKey(m) {
  if (m.raw?.created_at) {
    const t = new Date(m.raw.created_at).getTime();
    if (!Number.isNaN(t)) return t;
  }
  if (typeof m.id === "number" && !Number.isNaN(m.id)) return m.id;
  return 0;
}

function messageDayDate(m) {
  const iso = m.created_at ?? m.raw?.created_at;
  if (!iso) return null;
  const d = typeof iso === "string" ? parseISO(iso) : new Date(iso);
  return isValid(d) ? d : null;
}

function showDaySeparatorBefore(messages, index) {
  const d = messageDayDate(messages[index]);
  if (!d) return false;
  if (index === 0) return true;
  const prev = messageDayDate(messages[index - 1]);
  if (!prev) return true;
  return startOfDay(d).getTime() !== startOfDay(prev).getTime();
}

/** First row in chat: original contact-request note — same bubble UI as other received messages */
const ORIGINAL_REQUEST_ROW_ID = "__contact_request_original__";

/** Composer grows 1→3 lines; beyond that height stays at 3 lines with internal scrollbar */
const COMPOSER_MIN_H = 40;
const COMPOSER_THREE_LINE_H = 78;

/**
 * Shared contact-request chat (recruiter table + candidate notifications).
 * Same APIs, Pusher channel, and bubble UI as recruiter Contact Requests.
 */
export default function ContactRequestChatDialog({
  isOpen,
  onClose,
  userID,
  contactRequestId,
  contactRequestRecord = null,
  initialReceiverUserId = null,
  chatFallbackNote = "",
  headerTitle,
  headerEmail = "",
  headerPhone = "",
  jobContext = null,
}) {
  const { showToast } = useToastStore;

  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const [chatReceiverUserId, setChatReceiverUserId] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);

  useEffect(() => {
    const { setContactRequestChatDialogOpen } = useGlobalStore.getState();
    setContactRequestChatDialogOpen(
      Boolean(isOpen),
      isOpen ? contactRequestId : null,
    );
    return () => {
      setContactRequestChatDialogOpen(false, null);
    };
  }, [isOpen, contactRequestId]);

  const prevChatOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      prevChatOpenRef.current = true;
      return;
    }
    if (!prevChatOpenRef.current) return;
    prevChatOpenRef.current = false;
    void useGlobalStore.getState().refreshNotificationBellFromServer();
  }, [isOpen]);

  const resetAndClose = () => {
    setChatMessages([]);
    setMessageInput("");
    setChatReceiverUserId(null);
    setChatLoading(false);
    setChatSending(false);
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) return;
    const rid =
      initialReceiverUserId != null && initialReceiverUserId !== ""
        ? Number(initialReceiverUserId)
        : null;
    if (rid != null && !Number.isNaN(rid)) {
      setChatReceiverUserId(rid);
    } else {
      setChatReceiverUserId(null);
    }
    setMessageInput("");
  }, [isOpen, contactRequestId, initialReceiverUserId]);

  useEffect(() => {
    if (!isOpen || contactRequestId == null) return;

    let cancelled = false;

    (async () => {
      setChatLoading(true);
      void InterViewSlotService.markContactRequestMessagesRead(
        contactRequestId,
      ).catch((err) => {
        console.warn("markContactRequestMessagesRead:", err);
      });
      try {
        const res = await InterViewSlotService.getContactRequestMessages(
          contactRequestId,
        );
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        const inferredReceiver = inferReceiverFromMessages(list, userID);
        if (inferredReceiver != null) {
          setChatReceiverUserId((prev) => prev ?? inferredReceiver);
        } else if (contactRequestRecord) {
          const fromRow = resolveChatReceiverUserId(
            contactRequestRecord,
            userID,
          );
          if (fromRow != null) {
            setChatReceiverUserId((prev) => prev ?? fromRow);
          }
        }
        const mapped = list.map((m) => mapMessageToBubble(m, userID));
        setChatMessages(mapped);
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          showToast({
            message: err.message || "Failed to load chat history",
            type: "error",
          });
          setChatMessages([]);
        }
      } finally {
        if (!cancelled) setChatLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    contactRequestId,
    chatFallbackNote,
    userID,
    contactRequestRecord,
  ]);

  useEffect(() => {
    if (!isOpen || contactRequestId == null) return;

    const appendIncoming = (payload) => {
      const apiMsg = payload?.message ?? payload;
      if (!apiMsg || apiMsg.id == null) return;
      void InterViewSlotService.markContactRequestMessagesRead(
        contactRequestId,
      ).catch((err) => {
        console.warn("markContactRequestMessagesRead (realtime):", err);
      });
      const other = otherPartyUserIdFromMessage(apiMsg, userID);
      if (other != null) {
        setChatReceiverUserId((prev) => prev ?? other);
      }
      setChatMessages((prev) => {
        if (prev.some((m) => m.id === apiMsg.id)) return prev;
        return [...prev, mapMessageToBubble(apiMsg, userID)];
      });
    };

    const onRequestChannel = (payload) => appendIncoming(payload);

    /** Matches backend: Echo.private(`contact-request.${userId}`) → private-contact-request.{userId} */
    const onUserChannel = (event) => {
      const crId = event?.contact_request_id ?? event?.contactRequestId;
      if (crId == null || Number(crId) !== Number(contactRequestId)) return;
      const apiMsg = apiMessageFromUserChannelEvent(event);
      if (apiMsg) appendIncoming({ message: apiMsg });
    };

    const chReqName = `private-contact-request.${contactRequestId}`;
    const chReq = pusher.subscribe(chReqName);
    chReq.bind("ContactRequestMessageSent", onRequestChannel);
    chReq.bind(".ContactRequestMessageSent", onRequestChannel);

    let chUser = null;
    if (userID != null && userID !== "") {
      const chUserName = `private-contact-request.${userID}`;
      if (chUserName !== chReqName) {
        chUser = pusher.subscribe(chUserName);
        chUser.bind(".contact-request.message.sent", onUserChannel);
        chUser.bind("contact-request.message.sent", onUserChannel);
      }
    }

    return () => {
      chReq.unbind("ContactRequestMessageSent", onRequestChannel);
      chReq.unbind(".ContactRequestMessageSent", onRequestChannel);
      pusher.unsubscribe(chReqName);
      if (chUser) {
        chUser.unbind(".contact-request.message.sent", onUserChannel);
        chUser.unbind("contact-request.message.sent", onUserChannel);
        pusher.unsubscribe(`private-contact-request.${userID}`);
      }
    };
  }, [isOpen, contactRequestId, userID]);

  const handleSendMessage = async () => {
    const trimmed = messageInput.trim();
    if (!trimmed || contactRequestId == null) return;

    const receiverId = pickSendReceiverId({
      storedReceiverId: chatReceiverUserId,
      activeRequest: contactRequestRecord,
      chatMessages,
      userID,
    });

    if (receiverId == null) {
      showToast({
        message:
          "Could not determine who to reply to. Try again after messages load, or contact support.",
        type: "error",
      });
      return;
    }

    setChatSending(true);
    try {
      const res = await InterViewSlotService.sendContactRequestMessage(
        contactRequestId,
        {
          receiver_id: receiverId,
          message: trimmed,
        },
      );
      const apiMsg = res?.data;
      if (apiMsg) {
        const other = otherPartyUserIdFromMessage(apiMsg, userID);
        if (other != null) {
          setChatReceiverUserId((prev) => prev ?? other);
        }
        setChatMessages((prev) => {
          if (prev.some((m) => m.id === apiMsg.id)) return prev;
          return [...prev, mapMessageToBubble(apiMsg, userID)];
        });
      }
      setMessageInput("");
    } catch (err) {
      const data = err.response?.data;
      const apiMsg =
        (data?.errors &&
          Object.values(data.errors).flat().filter(Boolean)[0]) ||
        data?.message ||
        err.message ||
        "Failed to send message";
      showToast({
        message: apiMsg,
        type: "error",
      });
    } finally {
      setChatSending(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [chatMessages.length, chatFallbackNote, isOpen]);

  const syncComposerHeight = useCallback(() => {
    const el = messageInputRef.current;
    if (!el) return;
    el.style.height = "0px";
    const scrollH = el.scrollHeight;
    if (scrollH <= COMPOSER_THREE_LINE_H + 2) {
      el.style.height = `${Math.max(COMPOSER_MIN_H, Math.min(scrollH, COMPOSER_THREE_LINE_H))}px`;
      el.style.overflowY = "hidden";
    } else {
      el.style.height = `${COMPOSER_THREE_LINE_H}px`;
      el.style.overflowY = "auto";
    }
  }, []);

  useEffect(() => {
    syncComposerHeight();
  }, [messageInput, syncComposerHeight]);

  useEffect(() => {
    if (isOpen) syncComposerHeight();
  }, [isOpen, syncComposerHeight]);

  const showFooter =
    jobContext &&
    (jobContext.vacancyTitle ||
      jobContext.companyName ||
      jobContext.campaignName ||
      jobContext.vacancyStage);

  const pinnedText =
    typeof chatFallbackNote === "string" ? chatFallbackNote.trim() : "";
  const sortedApi = [...chatMessages].sort(
    (a, b) => messageSortKey(a) - messageSortKey(b),
  );

  let messagesForDisplay = sortedApi;
  if (pinnedText) {
    const first = sortedApi[0];
    const alreadyInThread =
      first && String(first.content ?? "").trim() === pinnedText;
    if (!alreadyInThread) {
      messagesForDisplay = [
        {
          id: ORIGINAL_REQUEST_ROW_ID,
          sender: "received",
          content: pinnedText,
          created_at: null,
          raw: null,
        },
        ...sortedApi,
      ];
    }
  }

  if (!isOpen || contactRequestId == null) return null;

  return (
    <DialogBoxSkeleton isOpen={isOpen} onClose={resetAndClose}>
      <div className="flex max-h-[min(620px,90vh)] h-[min(620px,90vh)] w-full max-w-[50vw] h-auto flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl animate-in fade-in zoom-in duration-200">
        <div className="flex items-start justify-between px-6 py-5 border-b border-border bg-gradient-to-r from-primary/10 via-primary/5 to-white flex-shrink-0">
          <div className="w-full">
            <div className="grid gap-2">
              <h2 className="text-lg font-bold leading-tight text-foreground">
                {headerTitle}
              </h2>
              <div className="flex flex-wrap gap-2">
                {headerEmail ? (
                  <span className="inline-flex items-center gap-2 text-xs sm:text-sm text-foreground bg-card/80 px-3 py-1.5 rounded-full border border-primary/15">
                    <Mail size={14} className="text-muted-foreground" />
                    {headerEmail}
                  </span>
                ) : null}
                {headerPhone ? (
                  <span className="inline-flex items-center gap-2 text-xs sm:text-sm text-foreground bg-card/80 px-3 py-1.5 rounded-full border border-primary/15">
                    <Phone size={14} className="text-muted-foreground" />
                    {headerPhone}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={resetAndClose}
            className=" rounded-full  text-muted-foreground hover:text-foreground "
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-card">
          <div className="flex min-h-0 flex-1 flex-col px-6 pb-1 pt-2">
            <div className="max-h-[300px] h-auto flex-1 overflow-y-auto rounded-xl border border-border p-5 custom-scrollbar">
            <div className="flex flex-col gap-3">
              {chatLoading && (
                <div className="flex justify-center py-6">
                  <Loader />
                </div>
              )}
              {!chatLoading && messagesForDisplay.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No messages yet. Send a reply below.
                </p>
              )}
              {!chatLoading &&
                messagesForDisplay.map((m, index) => {
                  const isSent = m.sender === "sent";
                  const isTopContactRequest =
                    m.id === ORIGINAL_REQUEST_ROW_ID ||
                    (Boolean(pinnedText) &&
                      index === 0 &&
                      String(m.content ?? "").trim() === pinnedText);
                  const sep = showDaySeparatorBefore(messagesForDisplay, index);
                  const dayForSep = sep ? messageDayDate(m) : null;

                  return (
                    <React.Fragment key={m.id}>
                      {dayForSep ? <ChatDaySeparator date={dayForSep} /> : null}
                      <ChatBubble
                        isSent={isSent}
                        highlight={isTopContactRequest}
                        createdAt={m.created_at}
                      >
                        {m.content}
                      </ChatBubble>
                    </React.Fragment>
                  );
                })}

              <div ref={messagesEndRef} />
            </div>
            </div>
          </div>

        <div className="flex w-full flex-shrink-0 flex-col">
        <div className="flex flex-shrink-0 items-end gap-2 border-t border-border bg-muted px-6 py-3 z-20">
          <textarea
            ref={messageInputRef}
            rows={1}
            placeholder="Type a message…"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            disabled={chatSending}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (!chatSending) handleSendMessage();
              }
            }}
            className="min-h-[40px] w-full flex-1 resize-none rounded-lg border border-border p-2 text-sm leading-snug text-foreground outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
          />
          <button
            type="button"
            disabled={chatSending || !messageInput.trim()}
            className="mb-1.5 flex-shrink-0 self-end disabled:cursor-not-allowed"
            aria-label="Send message"
            onClick={() => !chatSending && handleSendMessage()}
          >
            <Send
              size={18}
              className={
                chatSending || !messageInput.trim()
                  ? "text-muted-foreground"
                  : "cursor-pointer text-muted-foreground hover:scale-110"
              }
            />
          </button>
        </div>

        {showFooter ? (
          <div className="border-t border-border bg-muted px-6 pb-5 pt-2 flex-shrink-0">
            <h4 className="text-lg font-bold text-foreground mb-3">
              {jobContext.vacancyTitle || "—"}
            </h4>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-border bg-card p-3">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide leading-none mb-1">
                  Company
                </p>
                <p className="text-sm font-semibold text-foreground break-words leading-tight">
                  {jobContext.companyName || "—"}
                </p>
              </div>
              <div className="rounded-xl border border-border bg-card p-3">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide leading-none mb-1">
                  Campaign
                </p>
                <p className="text-sm font-semibold text-foreground break-words leading-tight">
                  {jobContext.campaignName || "—"}
                </p>
              </div>
              <div className="rounded-xl border border-border bg-card p-3">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide leading-none mb-1">
                  Stage
                </p>
                <span className="inline-flex items-center rounded-full bg-primary-dark text-primary px-2.5 py-0.5 text-xs font-semibold">
                  {jobContext.vacancyStage || "—"}
                </span>
              </div>
            </div>
          </div>
        ) : null}
        </div>
        </div>
      </div>
    </DialogBoxSkeleton>
  );
}
